import { Prompt, PromptCreateRequest, PromptUpdateRequest, PromptOwnerType, PromptTestRequest, PromptTestResponse } from '../../../types/backend';
import { useMemo } from 'react';
import { createAuthenticatedApiClient } from '../../../shared/api/apiClient';
import { rpcCall } from '../../../shared/api/rpcClient';
import { useAuth } from '../../auth/context/AuthContext';

export const createPromptService = (idToken: string) => {
  const client = createAuthenticatedApiClient(idToken);

  const service = {
    listGlobalPrompts: async (): Promise<Prompt[]> =>
      rpcCall<Prompt[]>(client, 'prompts.ListGlobalPrompts'),

    getAllPrompts: async (customerId: string): Promise<Prompt[]> => {
      const [customerPrompts, globalPrompts] = await Promise.all([
        rpcCall(client, 'prompts.ListCustomerPrompts', { customer_id: customerId }),
        rpcCall<Prompt[]>(client, 'prompts.ListGlobalPrompts'),
      ]);
      return [...customerPrompts, ...globalPrompts];
    },

    createPrompt: async (
      customerId: string,
      request: PromptCreateRequest,
      owner: PromptOwnerType
    ): Promise<Prompt> => {
      if (owner === 'customer') {
        return rpcCall(client, 'prompts.CreateCustomerPrompt', { customer_id: customerId, ...request as unknown as Record<string, unknown> });
      }
      return rpcCall(client, 'prompts.CreateGlobalPrompt', request as unknown as Record<string, unknown>);
    },

    updatePrompt: async (
      promptId: string,
      request: PromptUpdateRequest,
      owner: PromptOwnerType,
      customerId?: string
    ): Promise<Prompt> => {
      if (owner === 'customer') {
        return rpcCall(client, 'prompts.UpdateCustomerPrompt', { customer_id: customerId, prompt_id: promptId, ...request as unknown as Record<string, unknown> });
      }
      return rpcCall(client, 'prompts.UpdateGlobalPrompt', { prompt_id: promptId, ...request as unknown as Record<string, unknown> });
    },

    deletePrompt: async (
      promptId: string,
      owner: PromptOwnerType,
      customerId?: string
    ): Promise<void> => {
      if (owner === 'customer') {
        await rpcCall(client, 'prompts.DeleteCustomerPrompt', { customer_id: customerId, prompt_id: promptId });
      } else {
        await rpcCall(client, 'prompts.DeleteGlobalPrompt', { prompt_id: promptId });
      }
    },

    publishPrompt: async (
      promptId: string,
      owner: PromptOwnerType,
      customerId?: string
    ): Promise<Prompt> => {
      if (owner === 'customer') {
        return rpcCall(client, 'prompts.PublishCustomerPrompt', { customer_id: customerId, prompt_id: promptId });
      }
      return rpcCall(client, 'prompts.PublishGlobalPrompt', { prompt_id: promptId });
    },

    testPrompt: async (request: PromptTestRequest): Promise<PromptTestResponse> =>
      rpcCall(client, 'prompts.TestPrompt', request as unknown as Record<string, unknown>),

    getPromptVersion: async (
      promptId: string,
      version: string,
      owner: PromptOwnerType,
      customerId?: string
    ): Promise<Prompt> => {
      if (owner === 'customer') {
        return rpcCall(client, 'prompts.GetCustomerPromptVersion', { customer_id: customerId, prompt_id: promptId, version });
      }
      return rpcCall(client, 'prompts.GetGlobalPromptVersion', { prompt_id: promptId, version });
    }
  };

  return service;
};

export const usePromptService = () => {
  const { idToken } = useAuth();

  return useMemo(() => {
    if (!idToken) {
      throw new Error('usePromptService requires authentication');
    }

    return createPromptService(idToken);
  }, [idToken]);
};
